import { useEffect, useMemo, useState } from "react";
import "./DeleteModal.css";
import "./DeeplinkModal.css";
import { Box } from "@mui/material";
import {
  DeleteButton,
  PrimaryButton,
  SecondaryButton,
} from "../buttonComponent/ButtonComponent";
import TextInput from "../textInput/TextInput";
import DeleteModal from "./DeleteModal";
import DeeplinkModalHeader from "./DeeplinkModalHeader";
import DeeplinkInstitutionPicker from "./DeeplinkInstitutionPicker";
import { useAuth } from "../../config/utilities/useAuth";
import {
  APIPatchDeeplink,
  APIDeleteDeeplink,
} from "../../config/utilities/APIs/masterAdmin/deeplinksApi";

const boxStyle = {
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  width: "min(52rem, calc(100vw - 2rem))",
  overflow: "hidden",
  maxHeight: "92vh",
};

const isValidUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const getDeeplinkId = (deeplink) =>
  deeplink?.deeplinkId || deeplink?._id || "";

export default function EditDeeplinkModal({
  isOpen,
  onClose,
  deeplink,
  institutions = [],
  onSave,
  onDeleted,
  onDeleteError,
  onPatchError,
  fetchDeeplinks,
}) {
  const { token } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [input, setInput] = useState({
    label: "",
    url: "",
    institutionIds: [],
  });
  const [keyUp, setKeyUp] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  useEffect(() => {
    if (!isOpen || !deeplink) {
      setInitialSnapshot(null);
      return;
    }

    const institutionIds = Array.isArray(deeplink.institutionIds)
      ? [...deeplink.institutionIds]
      : [];

    const nextInput = {
      label: deeplink.label || "",
      url: deeplink.url || "",
      institutionIds,
    };

    setInput(nextInput);
    setInitialSnapshot({
      label: nextInput.label.trim(),
      url: nextInput.url.trim(),
      institutionIds: [...institutionIds].sort(),
    });
    setUrlError("");
  }, [isOpen, deeplink]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
    if (name === "url") setUrlError("");
  };

  const isFormValid = useMemo(() => {
    const label = input.label?.trim() || "";
    const url = input.url?.trim() || "";
    return label.length > 0 && label.length <= 60 && isValidUrl(url);
  }, [input]);

  const hasChanges = useMemo(() => {
    if (!initialSnapshot || !isOpen) return false;
    const institutionIds = [...(input.institutionIds || [])].sort();
    return (
      input.label.trim() !== initialSnapshot.label ||
      input.url.trim() !== initialSnapshot.url ||
      JSON.stringify(institutionIds) !==
        JSON.stringify(initialSnapshot.institutionIds)
    );
  }, [initialSnapshot, input, isOpen]);

  const handleSave = async () => {
    if (!deeplink || !isFormValid || !hasChanges || !token) return;

    const url = input.url.trim();
    if (!isValidUrl(url)) {
      setUrlError("Enter a valid http or https URL");
      return;
    }

    const body = {
      url,
      institutionIds: input.institutionIds || [],
      label: input.label.trim(),
    };

    const deeplinkId = getDeeplinkId(deeplink);
    const response = await APIPatchDeeplink(
      deeplinkId,
      body,
      setSaveLoading,
      token
    );

    if (response?.status >= 200 && response?.status < 300) {
      onSave?.();
      if (fetchDeeplinks) await fetchDeeplinks();
      onClose();
      return;
    }

    const message =
      response?.response?.data?.message ||
      response?.message ||
      "Failed to update deeplink";
    onPatchError?.(message);
  };

  const handleDelete = async () => {
    if (!deeplink || !token) return;

    const deeplinkId = getDeeplinkId(deeplink);
    const response = await APIDeleteDeeplink(
      deeplinkId,
      deeplink._id,
      setDeleteLoading,
      token
    );

    if (response?.status >= 200 && response?.status < 300) {
      setShowDeleteModal(false);
      onDeleted?.();
      if (fetchDeeplinks) await fetchDeeplinks();
      onClose();
      return;
    }

    const message =
      response?.response?.data?.message ||
      response?.message ||
      "Failed to delete deeplink";
    onDeleteError?.(message);
  };

  if (!isOpen || !deeplink) return null;

  return (
    <>
      <div className="modal-backdrop">
        <Box sx={boxStyle} className="deeplink-modal-box">
          <DeeplinkModalHeader
            title="Edit Deeplink"
            subtitle="Update the label, URL, or institution assignments."
            onClose={onClose}
          />

          <div className="user-modal-content deeplink-modal-content">
            <section className="deeplink-modal-section deeplink-modal-details">
              <div className="deeplink-modal-section-header">
                <h3 className="deeplink-modal-section-title">Deeplink details</h3>
                <p className="deeplink-modal-section-hint">
                  The label and URL shown in each institution&apos;s admin sidebar.
                </p>
              </div>
              <div className="deeplink-modal-section-body">
                <TextInput
                  label="Label"
                  placeholder="e.g. Parent portal"
                  input={input}
                  setInput={setInput}
                  name="label"
                  onChangeFunction={handleInputChange}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                  required
                />
                <TextInput
                  label="URL"
                  placeholder="https://school.busbuddy.app/parent-portal"
                  input={input}
                  setInput={setInput}
                  name="url"
                  onChangeFunction={handleInputChange}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                  error={Boolean(urlError)}
                  errorMessage={urlError}
                  required
                />
              </div>
            </section>

            <section className="deeplink-modal-section deeplink-modal-assignments">
              <div className="deeplink-modal-section-header">
                <h3 className="deeplink-modal-section-title">Linked institutions</h3>
                <p className="deeplink-modal-section-hint">
                  Choose which institution sidebars should show this link.
                </p>
              </div>
              <div className="deeplink-modal-section-body">
                <DeeplinkInstitutionPicker
                  institutions={institutions}
                  selectedIds={input.institutionIds || []}
                  onChange={(institutionIds) =>
                    setInput((prev) => ({ ...prev, institutionIds }))
                  }
                  isOpen={isOpen}
                />
              </div>
            </section>
          </div>

          <div className="button-container-delete deeplink-edit-modal-actions">
            <SecondaryButton
              width="12rem"
              label="Cancel"
              imageIcon={false}
              onClickFunction={onClose}
            />
            <PrimaryButton
              width="12rem"
              label="Save Changes"
              onClickFunction={handleSave}
              disable={!isFormValid || !hasChanges}
              loading={saveLoading}
            />
            <DeleteButton
              buttonWidth="12rem"
              buttonHeight="2.75rem"
              label="Delete"
              onClickFunction={() => setShowDeleteModal(true)}
            />
          </div>
        </Box>
      </div>

      <DeleteModal
        loading={deleteLoading}
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={{ label: deeplink.label }}
        module="deeplink"
        stackLevel={1}
      />
    </>
  );
}
